import { useState } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import storeLabel from '../../utils/storeLabel';

// Sentinel stored as a per-store override meaning "mirror & push the dashboard
// default for this field". Must match the backend (syncController USE_DEFAULT).
export const USE_DEFAULT = '__use_default__';

// Per-store override editor with a "Default" tab plus one tab per store. Each
// store field has three states:
//   • "Use the default" checked → override = USE_DEFAULT → the default value is
//     pushed to this store.
//   • a custom value entered      → that value is pushed.
//   • nothing (unchecked, empty)  → no override → the store keeps its own
//     content (nothing is pushed / overwritten).
const StoreTabPanel = ({
  stores,
  defaultValue,
  storeOverrides,
  onDefaultChange,
  onOverrideChange,
  renderEditor,
  label,
  useDefaultInitially = false,
}) => {
  const [tab, setTab] = useState(0);

  const currentStore = tab === 0 ? null : stores[tab - 1];
  const storeId = currentStore?.id;
  const override = storeId ? storeOverrides?.[storeId] : undefined;
  const usingDefault = override === USE_DEFAULT;
  const hasCustom = override !== undefined && !usingDefault;

  // Checkbox reflects the persisted state; falls back to useDefaultInitially only
  // when the store has never been configured.
  const useDefaultChecked = usingDefault || (override === undefined && useDefaultInitially);

  const getSeedValue = () => (Array.isArray(defaultValue) ? [] : '');

  const handleUseDefaultToggle = (checked) => {
    // Checked → push the default (sentinel). Unchecked → an explicit empty
    // custom value: the field is pushed as empty (clears it on the store) until
    // you type a custom value. (A never-configured store stays untouched.)
    onOverrideChange(storeId, checked ? USE_DEFAULT : getSeedValue());
  };

  return (
    <Box sx={{ mb: 3 }}>
      {label && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {label}
        </Typography>
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.85rem' },
          }}
        >
          <Tab label="Default" />
          {stores.map((s) => (
            <Tab
              key={s.id}
              label={storeLabel(s)}
            />
          ))}
        </Tabs>
      </Box>

      {tab === 0 ? (
        renderEditor(defaultValue, (val) => onDefaultChange(val), false)
      ) : (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={useDefaultChecked}
                onChange={(e) => handleUseDefaultToggle(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Use the default for this store (push the default value)
              </Typography>
            }
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
            Checked: this store receives the default value above on push. Unchecked: the store keeps
            its own value unless you enter a custom one here.
          </Typography>
          {useDefaultChecked
            ? renderEditor(defaultValue, () => {}, true)
            : renderEditor(
                hasCustom ? override : getSeedValue(),
                (val) => onOverrideChange(storeId, val),
                false
              )}
        </Box>
      )}
    </Box>
  );
};

export default StoreTabPanel;
